class BannerVideo {
	constructor(el) {
		this.el = el;
		this.iframe = this.el.querySelector('iframe');
		this.video = this.el.querySelector('video');
		this.videoUrl = this.el.getAttribute('data-video-url');
		this.videoType = this.el.getAttribute('data-video-type');
		this.bannerParentEl = this.el.closest('.banner');
		this.currentSize = '';
		this.iframeSrc = '';
		this.isPlaying = false;
		this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		this.toggleButton =
			this.bannerParentEl?.querySelector('[data-banner-video-toggle]') ||
			this.el.parentElement?.querySelector('[data-banner-video-toggle]');

		this.onResize = this.debounce(() => {
			this.fitCover();

			if (!this.videoType || this.videoType === 'responsive') {
				this.chooseVideoAndPoster();
			}
		}, 150);

		// Check if this is an iframe-based video (YouTube/Vimeo) or HTML5 video
		if (this.iframe && this.videoType === 'embed') {
			this.setupIframe();
		} else if (this.video && (this.videoType === 'local' || this.videoType === 'external')) {
			this.setupHTML5Video();
		} else if (this.el.tagName.toLowerCase() === 'video') {
			this.setupHTML5Video();
		} else {
			console.warn('BannerVideo: Invalid element. Please provide a video element or iframe with proper data attributes.');
		}

		this.setupToggleButton();
		this.setupCoverSizing();
	}

	debounce(fn, delay = 150) {
		let timeoutId;

		return (...args) => {
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(() => fn(...args), delay);
		};
	}

	setupCoverSizing() {
		this.fitCover();
		window.addEventListener('resize', this.onResize);

		if (typeof ResizeObserver !== 'undefined') {
			this.resizeObserver = new ResizeObserver(this.onResize);
			this.resizeObserver.observe(this.el);

			if (this.bannerParentEl) {
				this.resizeObserver.observe(this.bannerParentEl);
			}
		}
	}

	/**
	 * Size the media so it always covers the banner container (object-fit: cover math).
	 * Needed especially for iframes, which ignore object-fit.
	 */
	fitCover() {
		const media = this.iframe || this.video || (this.el.tagName?.toLowerCase() === 'video' ? this.el : null);

		if (!media) {
			return;
		}

		// HTML5 video uses CSS object-fit: cover — only force iframe dimensions.
		if (media.tagName?.toLowerCase() === 'video' && !this.iframe) {
			return;
		}

		const containerWidth = this.el.clientWidth;
		const containerHeight = this.el.clientHeight;

		if (!containerWidth || !containerHeight) {
			return;
		}

		const mediaRatio = 16 / 9;
		const containerRatio = containerWidth / containerHeight;

		let width;
		let height;

		if (containerRatio > mediaRatio) {
			width = containerWidth;
			height = containerWidth / mediaRatio;
		} else {
			height = containerHeight;
			width = containerHeight * mediaRatio;
		}

		media.style.width = `${Math.ceil(width)}px`;
		media.style.height = `${Math.ceil(height)}px`;
	}

	setupToggleButton() {
		if (!this.toggleButton) {
			return;
		}

		this.toggleButton.addEventListener('click', () => {
			if (this.isPlaying) {
				this.pause();
			} else {
				this.play();
			}
		});

		this.updateToggleUI();
	}

	updateToggleUI() {
		if (!this.toggleButton) {
			return;
		}

		const pauseIcon = this.toggleButton.querySelector(
			'.banner-default__video-toggle-icon--pause'
		);
		const playIcon = this.toggleButton.querySelector(
			'.banner-default__video-toggle-icon--play'
		);

		this.toggleButton.setAttribute('aria-pressed', this.isPlaying ? 'false' : 'true');
		this.toggleButton.setAttribute(
			'aria-label',
			this.isPlaying ? 'Pause background video' : 'Play background video'
		);

		if (pauseIcon) {
			pauseIcon.hidden = !this.isPlaying;
		}

		if (playIcon) {
			playIcon.hidden = this.isPlaying;
		}
	}

	play() {
		if (this.iframe && this.videoType === 'embed') {
			this.playIframe();
		} else if (this.video) {
			this.video
				.play()
				.then(() => {
					this.isPlaying = true;
					this.updateToggleUI();
				})
				.catch((error) => {
					console.warn('BannerVideo: Play failed:', error);
					this.setVideoAutoplaySupport(false, false);
				});
			return;
		}

		this.isPlaying = true;
		this.updateToggleUI();
	}

	pause() {
		if (this.iframe && this.videoType === 'embed') {
			this.pauseIframe();
		} else if (this.video) {
			this.video.pause();
		}

		this.isPlaying = false;
		this.updateToggleUI();
	}

	playIframe() {
		if (!this.iframe) {
			return;
		}

		if (this.iframeSrc) {
			this.iframe.setAttribute('src', this.iframeSrc);
			return;
		}

		this.setupIframe(true);
	}

	pauseIframe() {
		if (!this.iframe) {
			return;
		}

		const currentSrc = this.iframe.getAttribute('src');

		if (currentSrc) {
			this.iframeSrc = currentSrc;
			this.iframe.setAttribute('src', '');
		}
	}

	setupIframe(forcePlay = false) {
		const { iframe, videoUrl } = this;
		let src = this.convertToEmbedUrl(videoUrl);
		const shouldAutoplay = forcePlay || !this.prefersReducedMotion;

		if (src) {
			if (src.includes('youtube.com')) {
				const videoIdMatch = src.match(/\/embed\/([a-zA-Z0-9_-]+)/);
				const videoId = videoIdMatch ? videoIdMatch[1] : '';

				src += (src.includes('?') ? '&' : '?') + 'mute=1&loop=1';

				if (shouldAutoplay) {
					src += '&autoplay=1';
				}

				if (videoId) {
					src += `&playlist=${videoId}`;
				}

				const playlistMatch = src.match(/list=([a-zA-Z0-9_-]+)/);
				if (playlistMatch) {
					src += `&playlist=${playlistMatch[1]}`;
				}

				src += '&vq=hd1080';
			} else if (src.includes('vimeo.com')) {
				src += (src.includes('?') ? '&' : '?') + 'muted=1&loop=1&quality=1080p';

				if (shouldAutoplay) {
					src += '&autoplay=1';
				}
			} else {
				console.warn('BannerVideo: The video URL is not a recognized YouTube or Vimeo URL.');
				return;
			}

			this.iframeSrc = src;
			iframe.setAttribute('allow', 'autoplay; fullscreen');

			if (shouldAutoplay) {
				iframe.setAttribute('src', src);
				this.isPlaying = true;
			} else {
				this.isPlaying = false;
			}

			this.updateToggleUI();
		} else {
			console.warn('BannerVideo: Could not convert video URL to embed format.');
		}

		this.fitCover();
	}

	setupHTML5Video() {
		const videoElement = this.video || this.el;

		if (this.videoType === 'local' || this.videoType === 'external') {
			if (!videoElement.hasAttribute('loop')) {
				videoElement.setAttribute('loop', 'true');
			}

			videoElement.addEventListener('ended', () => {
				videoElement.currentTime = 0;
				if (this.isPlaying) {
					videoElement.play().catch((error) => {
						console.warn('BannerVideo: Loop replay failed:', error);
					});
				}
			});

			if (this.prefersReducedMotion) {
				videoElement.pause();
				this.isPlaying = false;
				this.updateToggleUI();
			} else {
				videoElement
					.play()
					.then(() => {
						this.isPlaying = true;
						this.updateToggleUI();
					})
					.catch((error) => {
						console.warn('BannerVideo: Autoplay failed:', error);
						this.isPlaying = false;
						this.updateToggleUI();
						this.setVideoAutoplaySupport(false, false);
					});
			}
		} else {
			this.chooseVideoAndPoster();
		}

		videoElement.addEventListener('error', this.catchPlaybackError);
		videoElement.addEventListener('abort', this.catchPlaybackError);
	}

	convertToEmbedUrl(url) {
		if (url.includes('youtube.com/watch')) {
			const videoId = url.match(/[?&]v=([^&]+)/);
			if (videoId) {
				return `https://www.youtube.com/embed/${videoId[1]}`;
			}
		}

		if (url.includes('youtu.be/')) {
			const videoId = url.match(/youtu\.be\/([^?]+)/);
			if (videoId) {
				return `https://www.youtube.com/embed/${videoId[1]}`;
			}
		}

		if (url.includes('vimeo.com/')) {
			const videoId = url.match(/vimeo\.com\/([^?]+)/);
			if (videoId) {
				return `https://player.vimeo.com/video/${videoId[1]}`;
			}
		}

		if (url.includes('youtube.com/embed') || url.includes('player.vimeo.com/video')) {
			return url;
		}

		return null;
	}

	setVideoAutoplaySupport(isSupported, removeEl = false) {
		const htmlEl = document.documentElement;

		if (isSupported) {
			htmlEl.classList.add('video-autoplay-support');
			htmlEl.classList.remove('no-video-autoplay-support');
		} else {
			htmlEl.classList.remove('video-autoplay-support');
			htmlEl.classList.add('no-video-autoplay-support');
		}

		if (removeEl) {
			this.el.remove();
		}
	}

	catchPlaybackError = () => {
		this.setVideoAutoplaySupport(false, false);
	};

	chooseVideoAndPoster = () => {
		const bannerWidth = window.innerWidth;
		let newSize = '';

		if (bannerWidth >= 1024) {
			newSize = 'desktop';
		} else if (bannerWidth >= 641) {
			newSize = 'tablet';
		} else {
			newSize = 'mobile';
		}

		if (this.currentSize === newSize) {
			return;
		}

		this.currentSize = newSize;

		let videoUrl = '';
		let posterUrl = '';

		switch (this.currentSize) {
			case 'desktop':
				videoUrl = this.el.getAttribute('data-video-desktop');
				posterUrl = this.el.getAttribute('data-poster-desktop');
				break;

			case 'tablet':
				videoUrl = this.el.getAttribute('data-video-tablet');
				posterUrl = this.el.getAttribute('data-poster-tablet');
				break;

			case 'mobile':
				videoUrl = this.el.getAttribute('data-video-mobile');
				posterUrl = this.el.getAttribute('data-poster-mobile');
				break;

			default:
				break;
		}

		if (videoUrl) {
			this.el.pause();

			this.el.setAttribute('src', videoUrl);
			this.el.setAttribute('poster', posterUrl);

			this.el.load();

			if (this.prefersReducedMotion) {
				this.isPlaying = false;
				this.updateToggleUI();
				return;
			}

			this.el
				.play()
				.then(() => {
					this.isPlaying = true;
					this.updateToggleUI();
				})
				.catch(() => {
					this.isPlaying = false;
					this.updateToggleUI();
				});
		} else {
			this.setVideoAutoplaySupport(false);
		}
	};
}

export default BannerVideo;
